import useSWR from 'swr'
import type {Iri} from '../../@types/Api'
import type {SectorCollection, SectorFull, SectorItem, SectorPartial} from '../../@types/entity/Sector'
import type {Grade} from '../../@types/entity/types'
import type {CollectionMap} from '../../@types/hydra/HydraCollection'
import type {FetchOption} from '../../components/EnhancedTable/types'
import type {PartialFloorPlanProjectSpec} from '../../floorPlan/FloorPlanProject'
import {useCurrentSpotIri} from '../../states/currentSpot'
import {createCollectionIri, createItemIri} from '../../utils/iri'
import type {ClimbingType} from '../../utils/scales/types'
import {
  type FilterOption,
  filterToQueryString,
  useDelete,
  useGet,
  useMap,
  usePatch,
  usePost,
  useUpsert,
} from '../useApi'
import {useAuthenticatedFetcher} from '../useAuth'
import {useMutateResource} from '../useMutate'
import {useParam} from '../../states/location'

export type SectorMap = CollectionMap<SectorPartial>

type GetResponse = {
  readonly sector?: SectorItem
  readonly error?: Error
}

type GetResponseFull = {
  readonly sector?: SectorFull
  readonly error?: Error
}

type CollectionResponse = {
  readonly sectors?: SectorCollection
  readonly error?: Error
}

type MapResponse = {
  readonly sectorMap?: SectorMap
  readonly error?: Error
}

type SectorCollectionFilter = {
  readonly spotIri?: Iri
  readonly spotIris?: Iri[]
}
const collectionFilterOptions: FilterOption<SectorCollectionFilter> = {
  spotIri: 'spot',
  spotIris: 'spot',
}

export const useSectorGet = (sectorIri: Iri | undefined): GetResponse => {
  const {data, error} = useGet(sectorIri)

  return {
    sector: data,
    error,
  }
}

export function useSectorGetFull(sectorIri: Iri | undefined): GetResponseFull {
  const {data, error} = useGet(sectorIri ? `${sectorIri}/full` : undefined)

  return {
    sector: data,
    error,
  }
}

export const useSectorCollection = (filter: SectorCollectionFilter | undefined, fetchOptions?: FetchOption, enabled = true): CollectionResponse => {
  const querystring = filterToQueryString<SectorCollectionFilter>(collectionFilterOptions, enabled ? filter : undefined, fetchOptions)
  const {data, error} = useSWR(enabled && querystring ? `${createCollectionIri('sectors')}?${querystring.toString()}` : null)
  return {
    sectors: data,
    error,
  }
}

export const useSectorIriFromLocation = (): string | undefined => {
  const sectorId = useParam('sectorId')

  return createItemIri('sectors', sectorId)
}

export const useSectorFilterFromCurrentSpot = (): SectorCollectionFilter => {
  const spotIri = useCurrentSpotIri()

  return {spotIri}
}

export const useSectorCollectionToMap = ({sectors, error}: CollectionResponse): MapResponse => {
  const sectorMap = useMap<SectorPartial>(sectors)

  if (error) {
    return {
      error,
    }
  }
  if (!sectors) {
    return {}
  }

  return {
    sectorMap,
  }
}

type UpdatePayload = Omit<CreatePayload, 'spot'>

type CreatePayload = {
  spot: Iri

  name?: string
  climbingType?: ClimbingType
  minGrade?: Grade
  maxGrade?: Grade
  floorPlan?: Blob
  floorPlanProject?: PartialFloorPlanProjectSpec | undefined
  targetByGrade?: Record<string, Grade>
}

function useFloorPlanUpload(): (floorPlan: Blob, sector: SectorPartial) => Promise<SectorItem> {
  const authenticatedFetcher = useAuthenticatedFetcher()
  const mutateResource = useMutateResource()

  return async (floorPlan: Blob, sector: SectorPartial) => {
    const data = new FormData()
    data.append('image', floorPlan)

    const response = await authenticatedFetcher(`${sector['@id']}/floorPlan`, {
      method: 'POST',
      body: data,
    })

    await mutateResource(response['@id'], response)

    return response
  }
}

export function useSectorCreate(): (payload: CreatePayload) => Promise<SectorItem> {
  const handler = usePost<SectorItem>('sectors')
  const floorPlanHandler = useFloorPlanUpload()

  return async (payload: UpdatePayload) => {
    const {floorPlan} = payload
    payload.floorPlan = undefined

    const response = await handler(payload)
    if (floorPlan) {
      return await floorPlanHandler(floorPlan, response)
    }
    return response
  }
}

export function useSectorUpdate(): (sector: SectorPartial, payload: UpdatePayload) => Promise<SectorItem> {
  const handler = usePatch<SectorItem>()
  const floorPlanHandler = useFloorPlanUpload()

  return async (sector: SectorPartial, payload: UpdatePayload) => {
    const {floorPlan} = payload
    payload.floorPlan = undefined

    const response = await handler(sector, payload)
    if (floorPlan) {
      return await floorPlanHandler(floorPlan, response)
    }
    return response
  }
}

export function useSectorUpsert(): (
spot: SectorPartial | undefined,
payload: UpdatePayload,
createExtraPayload?: Omit<CreatePayload, keyof UpdatePayload>,
) => Promise<SectorItem> {
  return useUpsert(useSectorCreate(), useSectorUpdate())
}

export function useSectorDelete(): (sector: SectorPartial) => Promise<void> {
  return useDelete()
}
